import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';

const ContactForm = ({ industryName }) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState("Other Service Business");

  useEffect(() => {
    if (industryName) {
      if (industryName.includes("Dentist")) setSelectedIndustry("Dental & Medical");
      else if (industryName.includes("Law") || industryName.includes("Legal")) setSelectedIndustry("Legal & Law Firms");
      else if (industryName.includes("Real Estate")) setSelectedIndustry("Real Estate");
      else if (industryName.includes("Med Spa") || industryName.includes("Beauty")) setSelectedIndustry("Other Service Business");
      else setSelectedIndustry(industryName);
    }
  }, [industryName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.target);
    
    try {
      const response = await fetch("https://formspree.io/f/mbgrqogg", {
        method: "POST",
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        alert("Oops! There was a problem submitting your form. Please try again.");
      }
    } catch (error) {
      alert("Oops! There was a problem submitting your form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section id="contact" className="py-24 bg-slate-950 flex items-center justify-center">
        <div className="max-w-xl w-full mx-4 bg-slate-900 border border-blue-500/50 rounded-3xl p-12 text-center shadow-[0_0_50px_rgba(37,99,235,0.1)]">
          <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon name="Check" className="text-4xl text-emerald-500" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">Request Received!</h2>
          <p className="text-slate-400 mb-8">
            Our onboarding specialist will reach out within the next 2 hours to activate your 3-Day Free Trial.
          </p>
          <button onClick={() => setIsSubmitted(false)} className="text-blue-400 font-semibold hover:text-blue-300 transition-colors">
            Send another message
          </button>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="py-24 bg-slate-950 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/5 blur-[100px] pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-600/5 blur-[100px] pointer-events-none"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6 leading-tight">
              Ready to Stop the <span className="text-blue-500">Revenue Bleed?</span>
            </h2>
            <p className="text-xl text-slate-400 mb-10 leading-relaxed">
              Start your 3-Day Free Trial today. No credit card required to get started. See exactly how many leads we can capture for your business.
            </p>
            
            <div className="space-y-6">
              {[
                { icon: "Shield", title: "Zero Risk Trial", desc: "No credit card required to start." },
                { icon: "Zap", title: "10-Min Setup", desc: "We handle the entire integration for you." },
                { icon: "UserCheck", title: "Dedicated Manager", desc: "Direct access to our onboarding team." }
              ].map((item, idx) => (
                <div key={idx} className="flex gap-4">
                  <div className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center shrink-0 border border-slate-700">
                    <SafeIcon name={item.icon} className="text-xl text-blue-400" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white">{item.title}</h4>
                    <p className="text-slate-400 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 md:p-10 shadow-2xl">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Full Name</label>
                  <input 
                    name="name"
                    type="text" 
                    required 
                    className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none transition-all" 
                    placeholder="John Doe" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Business Email</label>
                  <input 
                    name="email"
                    type="email" 
                    required 
                    className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none transition-all" 
                    placeholder="john@business.com" 
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Phone Number</label>
                <input 
                  name="phone"
                  type="tel" 
                  required 
                  className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none transition-all" 
                  placeholder="(555) 000-0000" 
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Industry</label>
                <select 
                  name="industry"
                  value={selectedIndustry} 
                  onChange={(e) => setSelectedIndustry(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                >
                  <option value="Trades & Home Services">Trades & Home Services</option>
                  <option value="Dental & Medical">Dental & Medical</option>
                  <option value="Legal & Law Firms">Legal & Law Firms</option>
                  <option value="Real Estate">Real Estate</option>
                  <option value="Other Service Business">Other Service Business</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">How many calls do you miss weekly?</label>
                <textarea 
                  name="message"
                  className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none transition-all h-24 resize-none" 
                  placeholder="Tell us about your current call volume..."
                ></textarea>
              </div>

              <button 
                disabled={isSubmitting}
                className={`w-full bg-blue-600 hover:bg-blue-500 text-white py-4 rounded-xl font-bold text-lg transition-all shadow-[0_0_20px_rgba(37,99,235,0.3)] flex items-center justify-center gap-2 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Sending...
                  </>
                ) : (
                  'Start My 3-Day Free Trial'
                )}
              </button>
              
              <p className="text-center text-xs text-slate-500">
                By clicking, you agree to our Terms of Service and Privacy Policy.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;